import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight } from 'lucide-react';

/**
 * ConsignStep2ArtworkDetails
 *
 * The "Artwork details" form panel of the consignment flow (step 2 of 4).
 * Extracted from `ConsignPage.jsx` to keep the parent page < 600 lines.
 *
 * Pure presentation — state stays in the parent. The parent passes:
 *   - formData / setFormData
 *   - aiSuggestions (truthy when prefilled from image analysis)
 *   - canProceed()  - guard on the Continue CTA
 *   - onBack() / onContinue()
 */
const Field = ({ label, hint, children }) => (
  <div>
    <label className="block text-sm font-medium mb-2">
      {label}{hint && <span className="text-xs text-blue-600 ml-1">{hint}</span>}
    </label>
    {children}
  </div>
);

const inputClass = "w-full px-4 py-3 border border-neutral-300 rounded focus:outline-none focus:border-black";

const ConsignStep2ArtworkDetails = ({
  formData,
  setFormData,
  aiSuggestions,
  canProceed,
  onBack,
  onContinue,
}) => {
  const set = (key) => (e) => setFormData({ ...formData, [key]: e.target.value });
  const aiHint = aiSuggestions ? "(AI detected - editable)" : null;

  return (
    <motion.div
      key="step2"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      data-testid="consign-step2"
    >
      <h2 className="text-3xl font-light mb-4">Artwork Details</h2>

      {aiSuggestions ? (
        <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mb-6">
          <p className="text-sm text-blue-900">
            ℹ️ <strong>AI Pre-filled fields below.</strong> Please review and edit any information as needed. All fields are fully editable.
          </p>
        </div>
      ) : (
        <p className="text-neutral-600 mb-8">
          Please provide details about your artwork. All fields are optional except Artist Name and Title.
        </p>
      )}

      <div className="space-y-6 mb-8">
        <div className="grid grid-cols-2 gap-6">
          <Field label="Artist Name *" hint={aiHint}>
            <input
              type="text"
              value={formData.artist_name}
              onChange={set('artist_name')}
              className={inputClass}
              placeholder="e.g., Banksy"
              data-testid="consign-artist-input"
            />
          </Field>
          <Field label="Title *" hint={aiHint}>
            <input
              type="text"
              value={formData.title}
              onChange={set('title')}
              className={inputClass}
              placeholder="e.g., Girl with Balloon"
              data-testid="consign-title-input"
            />
          </Field>
        </div>

        <div className="grid grid-cols-3 gap-6">
          <Field label="Year">
            <input type="text" value={formData.year} onChange={set('year')}
                   className={inputClass} placeholder="e.g., 2004" />
          </Field>
          <Field label="Medium">
            <input type="text" value={formData.medium} onChange={set('medium')}
                   className={inputClass} placeholder="e.g., Screenprint" />
          </Field>
          <Field label="Dimensions">
            <input type="text" value={formData.dimensions} onChange={set('dimensions')}
                   className={inputClass} placeholder="e.g., 70 x 50 cm" />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-6">
          <Field label="Edition Number">
            <input type="text" value={formData.edition_number} onChange={set('edition_number')}
                   className={inputClass} placeholder="e.g., 25/150 signed" />
          </Field>
          <Field label="Condition">
            <select value={formData.condition} onChange={set('condition')} className={inputClass}>
              <option value="">Select condition</option>
              <option value="Excellent">Excellent</option>
              <option value="Good">Good</option>
              <option value="Fair">Fair</option>
              <option value="Poor">Poor</option>
            </select>
          </Field>
        </div>

        <Field label="Provenance / Purchase History">
          <textarea
            value={formData.purchase_history}
            onChange={set('purchase_history')}
            rows={3}
            className={inputClass}
            placeholder="Where and when did you acquire this artwork?"
          />
        </Field>

        <Field label="Current Location">
          <input
            type="text"
            value={formData.current_location}
            onChange={set('current_location')}
            className={inputClass}
            placeholder="e.g., London, UK"
          />
        </Field>
      </div>

      <div className="flex gap-4">
        <button
          onClick={onBack}
          className="px-6 py-3 border border-neutral-300 hover:border-black transition-colors flex items-center gap-2"
          data-testid="consign-step2-back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
          Back
        </button>
        <button
          onClick={onContinue}
          disabled={!canProceed()}
          className="flex-1 py-4 bg-black text-white hover:bg-neutral-800 disabled:bg-neutral-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          data-testid="consign-step2-continue-btn"
        >
          Continue
          <ArrowRight className="w-5 h-5" />
        </button>
      </div>
    </motion.div>
  );
};

export default ConsignStep2ArtworkDetails;
